import pickBy from 'lodash/pickBy';
import omit from 'lodash/omit';
import noop from 'lodash/noop';

const thirtyMinutes = 30 * 60 * 1000;

export const WINDOW_LOAD_EVENT = 'load';
export const DOCUMENT_LOAD_COMPLETE = 'complete';

export class ScrollRestorationValue {
  constructor() {
    this.originalValue = window.history.scrollRestoration;
  }

  setToManual = () => {
    window.history.scrollRestoration = 'manual';
  };

  restore = () => {
    window.history.scrollRestoration = this.originalValue;
  };
}

export default class ScrollPersistence {
  static storageKey = 'rca-scrollPersistencePositions';

  static persistToStorage = (scrollPositions, storage = window.localStorage) =>
    storage.setItem(ScrollPersistence.storageKey, JSON.stringify(scrollPositions));

  static getFromStorage = (storage = window.localStorage) => {
    try {
      return JSON.parse(storage.getItem(ScrollPersistence.storageKey));
    } catch (e) {
      storage.removeItem(ScrollPersistence.storageKey);
      return {};
    }
  };

  static getScrollPosition = (
    x = window.scrollX,
    y = window.scrollY,
    now = Date.now(),
    offset = thirtyMinutes,
  ) => ({
    x,
    y,
    expiresAt: now + offset,
  });

  static restoreScrollPosition = (scrollPositions, scroll = window.scrollTo) =>
    scroll(scrollPositions.x, scrollPositions.y);

  static getScrollPositions = () => ScrollPersistence.getFromStorage() || {};

  static removeScrollPositionForLocation = (
    key,
    persist = ScrollPersistence.persistToStorage,
    positions = ScrollPersistence.getScrollPositions(),
  ) => persist(omit(positions, key));

  static purgeOutdatedPositions = (
    persist = ScrollPersistence.persistToStorage,
    positions = ScrollPersistence.getFromStorage(),
    now = Date.now(),
  ) => persist(pickBy(positions, (position) => position.expiresAt > now));

  static onNavigateAway = (
    location = window.location.href,
    persist = ScrollPersistence.persistToStorage,
    positions = ScrollPersistence.getScrollPositions(),
    position = ScrollPersistence.getScrollPosition(),
  ) =>
    persist({
      ...positions,
      [location]: position,
    });

  static onDocumentFinishedLoading = (
    fn,
    after = noop,
    docState = document.readyState,
    listenFor = window.addEventListener,
    unlistenFor = window.removeEventListener,
  ) => {
    if (docState === DOCUMENT_LOAD_COMPLETE) {
      fn();
      after();
    } else {
      listenFor(WINDOW_LOAD_EVENT, () => {
        unlistenFor(WINDOW_LOAD_EVENT);
        fn();
        after();
      });
    }
  };

  static onReturn(
    location = window.location.href,
    purgeOutdated = ScrollPersistence.purgeOutdatedPositions,
    positions = ScrollPersistence.getScrollPositions(),
    scrollToPosition = ScrollPersistence.restoreScrollPosition,
    removePosition = ScrollPersistence.removeScrollPositionForLocation,
    onLoad = ScrollPersistence.onDocumentFinishedLoading,
    scrollRestorationValue = new ScrollRestorationValue(),
  ) {
    scrollRestorationValue.setToManual();

    purgeOutdated();
    const persistedPosition = positions[location];

    if (persistedPosition) {
      onLoad(() => {
        scrollToPosition(persistedPosition);
        removePosition(location);
      }, scrollRestorationValue.restore);
    }
  }
}
